import { useEffect, useMemo, useState, type FormEvent } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Drawer } from "../Drawer";
import { Icon } from "../ui/Icon";
import { trpc } from "../../lib/trpc";

const CURRENCIES = ["TRY", "USD", "EUR", "GBP"] as const;
type Currency = (typeof CURRENCIES)[number];
const CUR_SYMBOL: Record<string, string> = { TRY: "₺", USD: "$", EUR: "€", GBP: "£" };

const todayStr = (): string => {
  const d = new Date();
  const m = `${d.getMonth() + 1}`.padStart(2, "0");
  const day = `${d.getDate()}`.padStart(2, "0");
  return `${d.getFullYear()}-${m}-${day}`;
};
function fmtMoney(n: number): string {
  return new Intl.NumberFormat("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 4 }).format(n);
}
function fmtDate(d: string | Date): string {
  return new Intl.DateTimeFormat("tr-TR", { day: "2-digit", month: "2-digit", year: "numeric" }).format(new Date(d));
}
function fmtPct(n: number): string {
  return new Intl.NumberFormat("tr-TR", { maximumFractionDigits: 1 }).format(Math.abs(n));
}

interface Props {
  open: boolean;
  stockCardId: string | null;
  cardCode?: string;
  cardName?: string;
  baseUomSymbol?: string | null;
  onClose: () => void;
}

/**
 * Cascade drawer — bir stok kartının birim fiyat geçmişi.
 * Kullanıcı yeni fiyat atar; tüm değişiklikler tarih + kullanıcı ile saklanır.
 * Fiyat davranışı (trend) grafiği + delta (önceki aynı para birimine göre %) gösterilir.
 */
export function StockPriceDrawer({ open, stockCardId, cardCode, cardName, baseUomSymbol, onClose }: Props) {
  const qc = useQueryClient();
  const [price, setPrice] = useState("");
  const [currency, setCurrency] = useState<Currency>("TRY");
  const [effectiveAt, setEffectiveAt] = useState("");
  const [note, setNote] = useState("");
  const [err, setErr] = useState<string | null>(null);

  const histQ = trpc.stockCard.priceHistory.useQuery({ stockCardId: stockCardId! }, { enabled: open && !!stockCardId });
  const setMut = trpc.stockCard.setPrice.useMutation();

  useEffect(() => {
    if (!open) return;
    setPrice("");
    setCurrency("TRY");
    setEffectiveAt(todayStr());
    setNote("");
    setErr(null);
    setMut.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, stockCardId]);

  const history = histQ.data ?? [];
  const current = history[0] ?? null;

  // Her satır için önceki AYNI para birimindeki fiyata göre yüzde değişim.
  const rows = useMemo(
    () =>
      history.map((r, i) => {
        let prevPrice: number | null = null;
        for (let j = i + 1; j < history.length; j++) {
          const older = history[j];
          if (older && older.currency === r.currency) { prevPrice = older.unitPrice; break; }
        }
        const deltaPct = prevPrice != null && prevPrice > 0 ? ((r.unitPrice - prevPrice) / prevPrice) * 100 : null;
        return { ...r, deltaPct };
      }),
    [history],
  );

  // Trend: güncel para biriminin noktaları, eskiden yeniye.
  const trend = useMemo(() => {
    if (!current) return [] as typeof history;
    return history.filter((h) => h.currency === current.currency).slice().reverse();
  }, [history, current]);
  const trendMax = trend.length ? Math.max(...trend.map((p) => p.unitPrice)) : 0;
  const trendMin = trend.length ? Math.min(...trend.map((p) => p.unitPrice)) : 0;
  const curSym = current ? CUR_SYMBOL[current.currency] ?? current.currency : "";

  async function submit(e: FormEvent) {
    e.preventDefault();
    setErr(null);
    const p = Number(price);
    if (!price || !(p > 0)) return setErr("Geçerli bir birim fiyat girin.");
    if (!stockCardId) return;
    try {
      await setMut.mutateAsync({
        stockCardId,
        unitPrice: p,
        currency,
        effectiveAt: effectiveAt ? new Date(effectiveAt).toISOString() : undefined,
        note: note || undefined,
      });
      await qc.invalidateQueries();
      setPrice("");
      setNote("");
    } catch {
      /* mutErr banner */
    }
  }

  const mutErr = setMut.error?.message ?? null;
  const perUom = baseUomSymbol ? ` / ${baseUomSymbol}` : "";

  return (
    <Drawer
      open={open}
      onClose={onClose}
      size="md"
      title="Birim Fiyat"
      subtitle={cardCode ? `${cardCode} — ${cardName ?? ""}` : "Stok kartı fiyat geçmişi"}
      footer={<button type="button" className="btn-md ghost" onClick={onClose}>Kapat</button>}
    >
      {/* Güncel fiyat */}
      <div
        style={{
          display: "flex", alignItems: "baseline", gap: 10, padding: "14px 16px", marginBottom: 16,
          background: "var(--primary-subtle)", border: "1px solid var(--neutral-200)", borderRadius: "var(--radius-md)",
        }}
      >
        <span style={{ fontSize: 12, color: "var(--neutral-500)", textTransform: "uppercase", letterSpacing: ".04em" }}>Güncel</span>
        {current ? (
          <>
            <span style={{ fontFamily: "var(--font-mono)", fontVariantNumeric: "tabular-nums", fontSize: 26, fontWeight: 700, color: "var(--neutral-900)" }}>
              {fmtMoney(current.unitPrice)}
            </span>
            <span style={{ fontSize: 15, color: "var(--neutral-600)" }}>{curSym}{perUom}</span>
            <span style={{ marginLeft: "auto", fontSize: 12, color: "var(--neutral-500)" }}>
              {fmtDate(current.effectiveAt)} · {current.createdByName}
            </span>
          </>
        ) : (
          <span style={{ fontSize: 14, color: "var(--neutral-500)" }}>Henüz fiyat atanmadı</span>
        )}
      </div>

      {/* Yeni fiyat ata */}
      <form onSubmit={submit} noValidate style={{ marginBottom: 18 }}>
        <span className="lbl" style={{ fontSize: 13, fontWeight: 600, display: "block", marginBottom: 8 }}>Yeni Fiyat Ata</span>
        {err || mutErr ? <div className="form-banner-err" role="alert">{err ?? mutErr}</div> : null}
        <div className="form-row" style={{ gridTemplateColumns: "1fr 1fr" }}>
          <div className="form-field">
            <label className="lbl" htmlFor="sp-price">Birim Fiyat <span className="req">*</span></label>
            <input id="sp-price" className="form-input" type="number" step="any" min={0} value={price} onChange={(e) => setPrice(e.target.value)} inputMode="decimal" required />
          </div>
          <div className="form-field">
            <label className="lbl" htmlFor="sp-cur">Para Birimi</label>
            <select id="sp-cur" className="form-select" value={currency} onChange={(e) => setCurrency(e.target.value as Currency)}>
              {CURRENCIES.map((c) => <option key={c} value={c}>{c} ({CUR_SYMBOL[c]})</option>)}
            </select>
          </div>
        </div>
        <div className="form-row" style={{ gridTemplateColumns: "1fr 1fr" }}>
          <div className="form-field">
            <label className="lbl" htmlFor="sp-date">Geçerlilik Tarihi</label>
            <input id="sp-date" className="form-input" type="date" value={effectiveAt} onChange={(e) => setEffectiveAt(e.target.value)} />
          </div>
          <div className="form-field">
            <label className="lbl" htmlFor="sp-note">Not</label>
            <input id="sp-note" className="form-input" type="text" value={note} onChange={(e) => setNote(e.target.value)} maxLength={500} placeholder="ör. tedarikçi zammı" />
          </div>
        </div>
        <button type="submit" className="btn-md primary" disabled={setMut.isPending} style={{ marginTop: 4 }}>
          <Icon name="plus" size={14} />{setMut.isPending ? "Kaydediliyor…" : "Fiyat Ata"}
        </button>
      </form>

      {/* Fiyat davranışı (trend) */}
      <div style={{ marginBottom: 18 }}>
        <span className="lbl" style={{ fontSize: 13, fontWeight: 600, display: "block", marginBottom: 6 }}>
          Fiyat Davranışı {current ? <span className="help" style={{ fontWeight: 400 }}>({current.currency})</span> : null}
        </span>
        {trend.length < 2 ? (
          <div className="help" style={{ padding: "8px 0" }}>Davranış grafiği için en az 2 fiyat kaydı gerekir.</div>
        ) : (
          <>
            <div className="bar-chart" role="img" aria-label={`Fiyat davranışı: ${trend.map((p) => `${fmtDate(p.effectiveAt)} ${fmtMoney(p.unitPrice)} ${curSym}`).join("; ")}`}>
              {trend.map((p) => {
                const h = trendMax === trendMin ? 100 : 15 + 85 * ((p.unitPrice - trendMin) / (trendMax - trendMin));
                return <div key={p.id} className="bar" data-val={`${fmtMoney(p.unitPrice)} ${curSym} · ${fmtDate(p.effectiveAt)}`} style={{ height: `${h}%` }} />;
              })}
            </div>
            <div className="chart-x">
              {trend.map((p) => <span key={p.id}>{fmtDate(p.effectiveAt).slice(0, 5)}</span>)}
            </div>
          </>
        )}
      </div>

      {/* Geçmiş tablosu */}
      <div>
        <span className="lbl" style={{ fontSize: 13, fontWeight: 600, display: "block", marginBottom: 6 }}>Geçmiş</span>
        {histQ.isLoading ? (
          <div className="help" style={{ padding: "8px 0" }}>Yükleniyor…</div>
        ) : rows.length === 0 ? (
          <div className="help" style={{ padding: "8px 0" }}>Kayıt yok.</div>
        ) : (
          <table className="dt2">
            <thead>
              <tr>
                <th style={{ width: 96 }}>Tarih</th>
                <th className="num">Birim Fiyat</th>
                <th className="num" style={{ width: 84 }}>Değişim</th>
                <th>Kullanıcı</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={r.id}>
                  <td style={{ whiteSpace: "nowrap" }}>{fmtDate(r.effectiveAt)}</td>
                  <td className="cell-num">
                    {fmtMoney(r.unitPrice)}<span className="unit">{CUR_SYMBOL[r.currency] ?? r.currency}</span>
                    {i === 0 ? <span className="badge-pill b-success" style={{ marginLeft: 6 }}>Güncel</span> : null}
                    {r.note ? <span className="sec">{r.note}</span> : null}
                  </td>
                  <td className="cell-num">
                    {r.deltaPct == null ? (
                      <span style={{ color: "var(--neutral-400)" }}>—</span>
                    ) : Math.abs(r.deltaPct) < 0.05 ? (
                      <span style={{ color: "var(--neutral-500)" }}>±0</span>
                    ) : (
                      <span style={{ color: r.deltaPct > 0 ? "var(--danger)" : "var(--success)", fontWeight: 600 }}>
                        {r.deltaPct > 0 ? "+" : "−"}%{fmtPct(r.deltaPct)}
                      </span>
                    )}
                  </td>
                  <td style={{ whiteSpace: "nowrap" }}>{r.createdByName}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </Drawer>
  );
}
